/**
 * Victory system — team survival check (v0M3.9).
 *
 * Every tick, count how many teams are still in the game under the
 * campaign's `victoryCondition`. Neutral factions are excluded from the
 * calculus. If exactly one team survives → team-victory. If zero survive
 * (mutual elimination on the same tick) → draw. Otherwise ongoing.
 *
 * **destroy-capitals** — a team survives if any of its factions still
 * owns its capital's province. Capital = each faction's largest-pop
 * city (matches the AI's proxy from v0M3.8). Computed dynamically per
 * tick.
 *
 * **destroy-cities** — a team survives if any of its factions still
 * owns at least one province.
 *
 * Once `state.victoryResult.kind !== 'ongoing'`, the system is a no-op.
 * Client is expected to freeze the sim (pause driver) so the check
 * doesn't churn.
 */

import type { Command } from './commands';
import type { GameState, VictoryResult } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import type { VictoryCondition } from './scenario';
import { NEUTRAL_TEAM_ID } from './scenario';

const DEFAULT_CONDITION: VictoryCondition = 'destroy-capitals';

export function victorySystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  if (state.victoryResult.kind !== 'ongoing') return state;
  const condition = context.victoryCondition ?? DEFAULT_CONDITION;

  const surviving = survivingTeams(state, condition);
  if (surviving.size === 1) {
    const [winningTeamId] = surviving;
    state.victoryResult = { kind: 'team-victory', winningTeamId: winningTeamId!, condition };
    return state;
  }
  if (surviving.size === 0) {
    state.victoryResult = { kind: 'draw', condition };
    return state;
  }
  // MFix11.5 (2026-07-27) — result is still 'ongoing' (multi-team survives).
  // Check whether the human player faction has been eliminated. If so, fire
  // 'player-defeated' so the client can pause + show a "Defeat" banner
  // instead of leaving the player watching an unwinnable sim.
  //
  // Player = the (sole) non-AI, non-neutral faction in the campaign per the
  // v1 SP posture (§6.1 #19; MP renegotiates this). Also require that the
  // player HAD a capital at some point (capitalSettlementId set at
  // bootstrap) — protects test fixtures without capital assignment from
  // firing defeat on tick 1.
  const playerFactionId = findPlayerFactionId(state);
  if (playerFactionId !== null) {
    const player = state.factions.get(playerFactionId)!;
    const hadCapitalAssignment = player.capitalSettlementId !== undefined;
    if (hadCapitalAssignment && !surviving.has(player.teamId)) {
      state.victoryResult = { kind: 'player-defeated', condition };
    }
  }
  return state;
}

/**
 * Return the id of the sole non-AI, non-neutral faction (the human
 * player). Returns null if there is no such faction (all-AI test
 * scenario) or if there are multiple non-AI factions (post-MVP MP
 * shape — the `player-defeated` variant is SP-only at v1 and needs
 * renegotiation for MP per §6.1 #19).
 */
function findPlayerFactionId(state: GameState): import('./state').FactionId | null {
  let candidate: import('./state').FactionId | null = null;
  for (const factionId of state.factions.keys()) {
    const faction = state.factions.get(factionId)!;
    if (faction.teamId === NEUTRAL_TEAM_ID) continue;
    if (faction.isAI) continue;
    if (candidate !== null) return null; // Multiple non-AI factions — not SP.
    candidate = factionId;
  }
  return candidate;
}

/**
 * Return the set of teamIds still in the game under `condition`.
 * Neutrals (teamId = NEUTRAL_TEAM_ID) are always excluded — they aren't
 * players.
 */
function survivingTeams(state: GameState, condition: VictoryCondition): Set<number> {
  const surviving = new Set<number>();
  switch (condition) {
    case 'destroy-capitals':
      for (const factionId of state.factions.keys()) {
        const faction = state.factions.get(factionId)!;
        if (faction.teamId === NEUTRAL_TEAM_ID) continue;
        if (factionHoldsCapital(state, factionId)) surviving.add(faction.teamId);
      }
      break;
    case 'destroy-cities':
      for (const factionId of state.factions.keys()) {
        const faction = state.factions.get(factionId)!;
        if (faction.teamId === NEUTRAL_TEAM_ID) continue;
        if (factionOwnsAnyProvince(state, factionId)) surviving.add(faction.teamId);
      }
      break;
  }
  return surviving;
}

/**
 * "Does this faction still hold their capital?" Capital = the specific
 * Settlement entity recorded on `Faction.capitalSettlementId` at scenario bootstrap
 * (largest-pop city among the faction's starting provinces).
 *
 * Returns false if capitalSettlementId is unset (test fixtures with no
 * bootstrap-assigned capital — those factions are excluded from
 * destroy-capitals unless the caller explicitly configures otherwise).
 */
function factionHoldsCapital(state: GameState, factionId: import('./state').FactionId): boolean {
  const faction = state.factions.get(factionId);
  if (!faction || faction.capitalSettlementId === undefined) return false;
  const city = state.settlements.get(faction.capitalSettlementId);
  if (!city) return false;
  const province = state.provinces.get(city.provinceId);
  if (!province) return false;
  return province.ownerFactionId === factionId;
}

function factionOwnsAnyProvince(state: GameState, factionId: import('./state').FactionId): boolean {
  for (const provinceId of state.provinces.keys()) {
    const province = state.provinces.get(provinceId)!;
    if (province.ownerFactionId === factionId) return true;
  }
  return false;
}

// Re-export for consumers (client).
export type { VictoryResult };
